import React from "react";
import { registerView, View } from "@frui.ts/views";
import AccountingDocumentApprovalDetailViewModel from "viewModels/finance/accountingDocumentsApprovals/accountingDocumentApprovalDetailViewModel";
import type { ViewComponent } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import { Col, Form, Modal } from "react-bootstrap";
import FakeInput from "@edaz/edazkit/controls/FakeInput";
import { formatAmount, formatDate } from "utils/localizations";
import { isValidForApproval } from "utils/approvable";
import ApproveToolbox from "../../common/approval/approveToolbox";
import type AccountingDocumentCandidateDetailResponse from "entities/accountingDocumentCandidateDetailResponse";
import PrevNextNavigationHeader from "../../common/prevNextNavigationHeader";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay";
import { emptyValue } from "utils/emptyValue";

export const AccountingDocumentApprovalDetailView: ViewComponent<AccountingDocumentApprovalDetailViewModel> = observer(
  ({ vm }) => {
    return (
      <Modal scrollable show={true} onHide={vm.requestClose} centered dialogClassName="modal-90w">
        <Modal.Header closeButton={true}>
          <div className="d-flex align-items-center">
            <PrevNextNavigationHeader vm={vm} />
            <div className="ml-auto col-12 col-lg-auto">
              {vm.item && isValidForApproval(vm.item) && (
                <ApproveToolbox<AccountingDocumentCandidateDetailResponse>
                  approval={vm.item}
                  translate={vm.translate}
                  approve={vm.onApproveClick}
                  canApprove={vm.canApprove}
                  disabled={!vm.allowApprove}
                  myApprovalStep={vm.myApprovalStep}
                  approvedByMe={vm.approvedByMe}
                  isExported={vm.isExported}
                />
              )}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {vm.item ? (
            <LoaderOverlay loading={vm.busyWatcher.isBusy}>
              <div className="mb-6 row">
                <Form.Group as={Col} md={4} lg={3}>
                  <Form.Label>{vm.translateAttribute("accountingDate")}</Form.Label>
                  <FakeInput target={vm.item} property="accountingDate" valueFormatter={value => formatDate(value)} />
                </Form.Group>
                <Form.Group as={Col} md={4} lg={2}>
                  <Form.Label>{vm.translateAttribute("accountingPeriod")}</Form.Label>
                  <FakeInput target={vm.item} property="accountingPeriod" />
                </Form.Group>
                <Form.Group as={Col} md={4} lg={2}>
                  <Form.Label>{vm.translateAttribute("creditSum")}</Form.Label>
                  <FakeInput target={vm.item} property="creditSum" valueFormatter={value => formatAmount(value)} />
                </Form.Group>
                <Form.Group as={Col} md={4} lg={2}>
                  <Form.Label>{vm.translateAttribute("debitSum")}</Form.Label>
                  <FakeInput target={vm.item} property="debitSum" valueFormatter={value => formatAmount(value)} />
                </Form.Group>
                <Form.Group as={Col} md={4} lg={3}>
                  <Form.Label>{vm.translateAttribute("sequenceName")}</Form.Label>
                  <FakeInput target={vm.item} property="sequenceName" />
                </Form.Group>
                <Form.Group as={Col} md={4} lg={3}>
                  <Form.Label>{vm.translateAttribute("documentHeaderDescription")}</Form.Label>
                  <FakeInput target={vm.item} property="documentHeaderDescription" />
                </Form.Group>
                <Form.Group as={Col} md={8} lg={6}>
                  <Form.Label>{vm.translateAttribute("accountingTemplateId")}</Form.Label>
                  <FakeInput
                    target={vm.item}
                    property="accountingTemplateId"
                    valueFormatter={value => vm.translateAccountingTemplate(value)}
                  />
                </Form.Group>
                <Form.Group as={Col} md={6} lg={3}>
                  <Form.Label>{vm.translateAttribute("chargeId")}</Form.Label>
                  <FakeInput target={vm.item} property="chargeId" valueFormatter={value => value ?? emptyValue} />
                </Form.Group>
                <Form.Group as={Col} md={6} lg={3}>
                  <Form.Label>{vm.translate("paymentId")}</Form.Label>
                  <FakeInput
                    target={vm.item}
                    property={vm.item?.payGatePaymentId ? "payGatePaymentId" : "transferPaymentId"}
                    valueFormatter={value => value ?? emptyValue}
                  />
                </Form.Group>
              </div>
              <View vm={vm.accountingDocumentsRows} />
            </LoaderOverlay>
          ) : (
            <LoaderOverlay loading={true} style={{ minHeight: 200 }} />
          )}
        </Modal.Body>
      </Modal>
    );
  }
);

registerView(AccountingDocumentApprovalDetailView, AccountingDocumentApprovalDetailViewModel);
